<template>
  <Modal
    :is-open="isOpen"
    :buttons="[]"
    :show-dismiss-button="showCloseButton"
    :disable-close="disableClose || isConfirming"
    :min-width="320"
    :max-width="maxWidth"
    @close-modal="onClose"
  >
    <div class="text-center px-1 sm:px-4 pb-1 sm:pb-2">
      <!-- Severity icon (hidden when icon resolution is explicitly disabled) -->
      <div
        v-if="resolvedIcon"
        class="flex justify-center text-3xl sm:text-4xl py-3 sm:py-4"
      >
        <Icon
          :type="resolvedIcon"
          :severity="iconSeverity"
          featured
        />
      </div>

      <!-- Title -->
      <div
        v-if="title"
        class="text-md sm:text-lg font-semibold"
      >
        {{ title }}
      </div>

      <!-- Description -->
      <div
        v-if="description"
        class="text-sm text-secondary mt-1 sm:mt-2"
      >
        {{ description }}
      </div>

      <!-- Optional extra content from the parent -->
      <slot></slot>

      <!-- Actions: full-width, equal-sized buttons -->
      <div
        class="grid gap-2 sm:gap-3 mt-4 sm:mt-6"
        :class="showCancelButton ? 'grid-cols-2' : 'grid-cols-1'"
      >
        <Button
          v-if="showCancelButton"
          :label="cancelText"
          severity="secondary"
          outlined
          block
          :disabled="isConfirming"
          @click="onCancel"
        />
        <Button
          :label="confirmText"
          :severity="confirmSeverity"
          block
          :is-loading="isConfirming"
          @click="onConfirm"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// components
import Modal from '@/components/modal/Modal.vue'
import Icon from '@/components/icon/Icon.vue'
import Button from '@/components/button/Button.vue'

/**
 * Visual severity of the confirmation dialog.
 * Drives the default icon, the icon color and the confirm button color.
 */
type ConfirmationSeverity = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info'

interface Props {
  /** Controls the visibility of the dialog */
  isOpen: boolean
  /** Visual severity of the dialog */
  severity?: ConfirmationSeverity
  /** Heading shown below the icon */
  title?: string | null
  /** Supporting text shown below the title */
  description?: string | null
  /** Icon type override. When omitted, a default icon is derived from the severity */
  icon?: string | null
  /** Label of the cancel button */
  cancelText?: string
  /** Label of the confirm button */
  confirmText?: string
  /** Whether the cancel button is shown */
  showCancelButton?: boolean
  /** Whether the dismiss (X) button is shown in the top-right corner */
  showCloseButton?: boolean
  /** Shows a spinner on the confirm button and blocks closing while true */
  isConfirming?: boolean
  /** Prevents the dialog from being dismissed via backdrop / X button */
  disableClose?: boolean
  /** Maximum width (px) of the dialog */
  maxWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'primary',
  title: null,
  description: null,
  icon: null,
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  showCancelButton: true,
  showCloseButton: true,
  isConfirming: false,
  disableClose: false,
  maxWidth: 420
})

const emit = defineEmits<{
  /** Fired when the dialog is dismissed (backdrop click or X button) */
  close: []
  /** Fired when the cancel button is clicked */
  cancel: []
  /** Fired when the confirm button is clicked */
  confirm: []
}>()

/** Default icon per severity, used when no explicit icon prop is provided */
const severityIcons: Record<ConfirmationSeverity, string> = {
  primary: 'question-circle',
  secondary: 'question-circle',
  success: 'circle-check',
  warning: 'triangle-exclamation',
  danger: 'triangle-exclamation',
  info: 'info-circle'
}

/** Icon color token per severity (maps to the --fg-* CSS variables used by Icon) */
const severityIconColors: Record<ConfirmationSeverity, string> = {
  primary: 'brand',
  secondary: 'tertiary',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  info: 'info'
}

/** The icon to display: explicit icon prop wins, otherwise derived from severity */
const resolvedIcon = computed<string>(() => props.icon || severityIcons[props.severity])

/** Color token passed to the Icon component */
const iconSeverity = computed<string>(() => severityIconColors[props.severity])

/** Severity passed to the confirm button so it matches the dialog intent */
const confirmSeverity = computed<ConfirmationSeverity>(() => props.severity)

/**
 * Notifies the parent that the dialog was dismissed without a decision.
 */
function onClose(): void {
  emit('close')
}

/**
 * Notifies the parent that the user rejected the action.
 */
function onCancel(): void {
  // Ignore clicks while the confirm action is still running
  if (props.isConfirming) return
  emit('cancel')
}

/**
 * Notifies the parent that the user accepted the action.
 */
function onConfirm(): void {
  // Prevent double submission while the confirm action is running
  if (props.isConfirming) return
  emit('confirm')
}
</script>
